<template>
  <div class="w-full border border-gray-200 rounded-lg
   bg-white hover:shadow-md transition-all duration-200"
       :class="hasValidationErrors || !hasValidDimension ?
        'border-red-300 bg-red-50' : 'border-gray-200'">

    <!-- Compact Question Card -->
    <div class="p-3">
      <div class="flex items-center justify-between">
        <!-- Left: Question Info -->
        <div class="flex-1 min-w-0">
          <!-- Top Row: Selection + Index + Question Texts (Stacked) -->
          <div class="flex items-start gap-3 mb-2">
            <!-- Selection Checkbox -->
            <div v-if="selectable" class="flex-shrink-0 flex items-center">
              <Checkbox
                :model-value="selected"
                :binary="true"
                @update:model-value="$emit('toggle-selected', question)"
              />
            </div>
            <!-- Question Number -->
            <div class="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-800
             rounded-full flex items-center justify-center text-xs font-bold">
              {{ questionIndex + 1 }}
            </div>

            <!-- Question Texts (Stacked) -->
            <div class="flex-1 min-w-0 space-y-1">
              <!-- English Text -->
              <div class="flex items-center gap-2">
                <span class="text-xs font-medium text-blue-600
                 bg-blue-50 px-2 py-1 rounded">EN</span>
                <p class="text-sm font-medium text-gray-900 truncate">
                  {{ question.properties?.content?.text_question?.en || 'No English text' }}
                </p>
              </div>

              <!-- German Text -->
              <div class="flex items-center gap-2">
                <span class="text-xs font-medium text-green-600
                 bg-green-50 px-2 py-1 rounded">DE</span>
                <p class="text-sm font-medium text-gray-900 truncate">
                  {{ question.properties?.content?.text_question?.de || 'No German text' }}
                </p>
              </div>
            </div>
          </div>

          <!-- Bottom Row: Labels + Actions -->
          <div class="flex items-center justify-between" :class="selectable ? 'ml-12' : 'ml-9'">
            <!-- Left: Status Labels -->
            <div class="flex items-center gap-2">
              <!-- Dimension Status -->
              <span
                :class="hasValidDimension ?
                 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
                class="px-2 py-1 rounded-full text-xs font-medium"
                :title="hasValidDimension ? 'Valid Dimension' : 'Invalid Dimension'"
              >
                {{ question.properties?.dimensionName || 'No Dim' }}
              </span>

              <!-- Question Type -->
              <span class="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs font-medium">
                {{ question.type || 'Unknown' }}
              </span>

              <!-- Positive/Negative -->
              <span
                :class="question.properties?.data?.positive ?
                 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
                class="px-2 py-1 rounded-full text-xs font-medium"
                :title="question.properties?.data?.positive
                 ? 'Positive Question' : 'Negative Question'"
              >
                {{ question.properties?.data?.positive ? 'Positive' : 'Negative' }}
              </span>

              <!-- Sticky badge -->
              <span
                v-if="question.sticky"
                class="bg-amber-100 text-amber-800 px-2 py-1 rounded-full text-xs font-medium"
                title="Sticky Question - appears in every rotation"
              >
                <i class="pi pi-bookmark-fill mr-1"></i>
                Sticky
              </span>

              <!-- Unsaved indicator -->
              <span
                v-if="!question._id"
                class="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium"
                title="Unsaved Question"
              >
                <i class="pi pi-clock mr-1"></i>
                Unsaved
              </span>
            </div>

            <!-- Right: Action Buttons -->
            <div class="flex items-center gap-1">
              <Button
                :icon="isExpanded ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"
                class="p-button-text p-button-sm"
                @click="toggleExpanded"
                v-tooltip.top="isExpanded ? 'Collapse' : 'Expand'"
              />
              <Button
                icon="pi pi-pencil"
                class="p-button-text p-button-sm text-blue-600"
                @click="toggleExpanded"
                v-tooltip.top="'Edit'"
              />
              <Button
                icon="pi pi-trash"
                class="p-button-text p-button-sm text-red-600"
                @click="handleDelete"
                v-tooltip.top="'Delete'"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Expanded View: Shows full details for editing -->
    <Transition name="expand" appear>
      <div v-if="isExpanded && question" class="mt-4 border-t border-gray-200 pt-4">
      <!-- Validation Status -->
      <div v-if="hasValidationErrors"
         class="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
        <div class="flex items-center space-x-2">
          <i class="pi pi-exclamation-triangle text-red-500"></i>
          <span class="text-sm font-medium text-red-800">Validation Issues</span>
        </div>
        <p class="text-xs text-red-600 mt-1">
          Missing required fields or invalid dimension
        </p>
      </div>

      <div v-else class="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
        <div class="flex items-center space-x-2">
          <i class="pi pi-check-circle text-green-500"></i>
          <span class="text-sm font-medium text-green-800">All Required Fields Present</span>
        </div>
      </div>

      <!-- Question Editor Component -->
      <div v-if="question" class="bg-gray-50 rounded-lg p-4">
        <QuestionEditor
          :key="`question-editor-${questionIndex}-${isExpanded}`"
          :question="question"
          :is-new="false"
          @update-question="handleUpdate"
          @save-new="handleSaveNew"
          @delete-question="handleDelete"
          @cancel="handleCancel"
        />
      </div>

      <!-- Collapse Button -->
      <div class="mt-4 flex justify-center">
        <Button
          label="Collapse"
          icon="pi pi-chevron-up"
          class="p-button-text p-button-secondary"
          @click="handleCancel"
        />
      </div>
      </div>
    </Transition>
  </div>
</template>

<script>
import QuestionEditor from './QuestionEditor.vue';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';

export default {
  name: 'QuestionListItem',
  components: { QuestionEditor, Button, Checkbox },
  emits: [
    'toggle-expanded',
    'toggle-selected',
    'update-question',
    'save-new',
    'delete-question',
    'cancel',
  ],
  props: {
    question: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    isExpanded: {
      type: Boolean,
      default: false,
    },
    hasValidDimension: {
      type: Boolean,
      default: true,
    },
    selected: {
      type: Boolean,
      default: false,
    },
    selectable: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    questionIndex() {
      return this.index;
    },
    hasValidationErrors() {
      // Simple validation - check for required fields
      const requiredFields = ['properties.content.text_question.en', 'properties.dimensionName'];
      return requiredFields.some((field) => {
        const value = field.split('.').reduce((obj, key) => obj?.[key], this.question);
        return !value;
      });
    },
  },
  methods: {
    toggleExpanded() {
      // Add a small delay to ensure DOM is ready
      this.$nextTick(() => {
        this.$emit('toggle-expanded');
      });
    },
    handleUpdate(updatedQuestion) {
      this.$emit('update-question', updatedQuestion);
    },
    handleSaveNew(newQuestion) {
      this.$emit('save-new', newQuestion);
    },
    handleDelete() {
      this.$emit('delete-question');
    },
    handleCancel() {
      this.$emit('cancel');
    },
  },
};
</script>

<style scoped>
/* Compact question item styling */
.truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Transition animations */
.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.expand-enter-from,
.expand-leave-to {
  opacity: 0;
  max-height: 0;
  transform: translateY(-10px);
}

.expand-enter-to,
.expand-leave-from {
  opacity: 1;
  max-height: 1000px;
  transform: translateY(0);
}
</style>
